import { useEffect, useMemo, useState } from 'react';

import {
  ChakraFlex,
  DatePickerV2,
  Divider,
  InquiryQuickNote,
  InquiryTargetAdmitDateTrackingCard,
  InquiryTrackingCard,
  InquiryTrackingCardType,
  SkeletonLoader,
} from '@equip.health/ui';
import noop from 'lodash/noop';
import './Tracking.css';
import { DateTime } from 'luxon';

import { createQuickNoteRequest } from '~/components/common/inquiry/drawer/util';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { toUTCMidnight } from '~/lib/util/date.util';
import { useGetNoteCommunicationMethodsEnumQuery } from '~/store/services/enumerations';
import {
  useUpdateInquiryNextContactDateMutation,
  useUpdateTargetAdmissionMutation,
} from '~/store/services/inquiry';
import { useCreateNoteMutation } from '~/store/services/notes/notes';

import { mapNoteCommunicationMethodsToDropdownOptions } from '../viewUtil';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

export interface TrackingProps {
  id: string;
  inquiryId: string;
  isLoading: boolean;
  isPatientPrimaryContact: boolean;
  isTrackingEligible: boolean;
  nextDate?: Date;
  previousDate?: Date;
  patientId: string;
  targetAdmissionDate?: Date;
  version: string;
}

const TRACKING_CARD_CONTAINER_CLASS = 'tracking-card-container';

export const Tracking = ({
  id,
  inquiryId,
  isLoading,
  isPatientPrimaryContact,
  nextDate: initialNextDate,
  patientId,
  previousDate: initialPreviousDate,
  targetAdmissionDate,
  isTrackingEligible,
  version,
}: TrackingProps) => {
  const topLevelId = `${id}__tracking`;

  const {
    currentUser: { userId: currentUserId },
  } = useGlobalContext();
  const [triggerUpdateInquiryNextContactDate] =
    useUpdateInquiryNextContactDateMutation();
  const [createNote] = useCreateNoteMutation();
  const [triggerUpdateTargetAdmissionMutation] =
    useUpdateTargetAdmissionMutation();

  const [isTargetAdmitDatePickerOpen, setIsTargetAdmitDatePickerOpen] =
    useState<boolean>(false);
  const [isNextDatePickerOpen, setIsNextDatePickerOpen] =
    useState<boolean>(false);
  const [selectedNextDate, setSelectedNextDate] = useState<Date | undefined>(
    initialNextDate,
  );
  const [selectedTargetDate, setSelectedTargetDate] = useState<
    Date | undefined
  >(targetAdmissionDate);
  const { showErrorToast } = useErrorToast();

  if (targetAdmissionDate !== selectedTargetDate) {
    setSelectedTargetDate(targetAdmissionDate);
  }

  const {
    data: noteCommunicationMethods,
    isLoading: isNoteCommunicationMethodsLoading,
  } = useGetNoteCommunicationMethodsEnumQuery();

  useEffect(() => {
    if (initialNextDate) {
      setSelectedNextDate(initialNextDate);
    }
  }, [initialNextDate]);

  const handleNextContactDateSelect = (date: Date) => {
    setSelectedNextDate(date);
    setIsNextDatePickerOpen(false);

    triggerUpdateInquiryNextContactDate({
      inquiryId,
      nextContactDate: toUTCMidnight(date),
      showConflictErrorModal: false,
      version,
    })
      .unwrap()
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.EDIT_INQUIRY_V2,
          error.status,
          error.data?.message,
        );
      });
  };

  const handleTargetAdmitDateSelect = (date: Date) => {
    setSelectedTargetDate(date);
    setIsTargetAdmitDatePickerOpen(false);

    triggerUpdateTargetAdmissionMutation({
      inquiryId,
      showConflictErrorModal: false,
      targetAdmissionDate: toUTCMidnight(date),
      version,
    })
      .unwrap()
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.EDIT_INQUIRY_V2,
          error.status,
          error.data?.message,
        );
      });
  };

  const handleSaveNote = (contactMethod: string, comment: string) => {
    const payload = createQuickNoteRequest({
      activityMedium: contactMethod,
      inquiryId,
      isPatientPrimaryContact,
      noteContent: comment,
      patientExternalId: patientId,
      userExternalId: currentUserId,
    });

    createNote(payload);
  };

  const communicationOptions = useMemo(() => {
    return mapNoteCommunicationMethodsToDropdownOptions(
      noteCommunicationMethods,
    );
  }, [noteCommunicationMethods]);

  return (
    <SkeletonLoader
      id={topLevelId}
      isLoaded={!isLoading && !isNoteCommunicationMethodsLoading}
      width="100%"
    >
      <ChakraFlex
        backgroundColor="neutral.background.primary"
        borderColor="neutral.line"
        borderRadius="12px"
        borderWidth="1px"
        direction="column"
        gap="8px"
        padding="12px"
        width="100%"
      >
        {isTrackingEligible && (
          <>
            <ChakraFlex
              className={TRACKING_CARD_CONTAINER_CLASS}
              direction="column"
            >
              <DatePickerV2
                id={`${topLevelId}-target-admit-date-picker`}
                isOpen={isTargetAdmitDatePickerOpen}
                // Minimum date is one year ago.
                minDate={DateTime.now().minus({ year: 1 }).toJSDate()}
                onClickOutside={() => setIsTargetAdmitDatePickerOpen(false)}
                onDateChange={handleTargetAdmitDateSelect}
                selectedDate={selectedTargetDate}
              />
              <InquiryTargetAdmitDateTrackingCard
                date={targetAdmissionDate}
                onClick={() => {
                  setIsTargetAdmitDatePickerOpen(true);
                }}
              />
            </ChakraFlex>
            <Divider id={topLevelId} />
          </>
        )}
        <ChakraFlex
          className={TRACKING_CARD_CONTAINER_CLASS}
          direction="column"
        >
          <DatePickerV2
            id={`${topLevelId}-date-picker`}
            isOpen={isNextDatePickerOpen}
            minDate={new Date()}
            onClickOutside={() => setIsNextDatePickerOpen(false)}
            onDateChange={handleNextContactDateSelect}
            selectedDate={selectedNextDate}
          />
          <InquiryTrackingCard
            date={selectedNextDate}
            id={`${topLevelId}-next`}
            onClick={() => setIsNextDatePickerOpen(true)}
            type={InquiryTrackingCardType.NEXT}
          />
        </ChakraFlex>
        <Divider id={topLevelId} />
        <ChakraFlex direction="column" position="relative">
          <InquiryQuickNote
            contactMethodOptions={communicationOptions}
            onSave={handleSaveNote}
            triggerContent={
              <InquiryTrackingCard
                date={initialPreviousDate}
                id={`${topLevelId}-previous`}
                onClick={noop} // Passing noop to trigger hover effect
                type={InquiryTrackingCardType.PREVIOUS}
              />
            }
          />
        </ChakraFlex>
      </ChakraFlex>
    </SkeletonLoader>
  );
};
